/*
 * These enums are the job API's wire format, not labels of our own: the backend serialises its Java
 * enums by name, in upper case, so every value here has to read exactly as the API sends it. They
 * were lower case, which made every comparison against them silently false: a job that had finished
 * looked to the caller like a job still running.
 */

export enum JobState {
    SUBMITTED = 'SUBMITTED',
    RUNNING = 'RUNNING',
    PAUSED = 'PAUSED',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED',
    CANCELLED = 'CANCELLED',
    TIMED_OUT = 'TIMED_OUT',
}

/** Spring Batch's own status, passed through as it stands. */
export enum BatchStatus {
    STARTING = 'STARTING',
    STARTED = 'STARTED',
    STOPPING = 'STOPPING',
    STOPPED = 'STOPPED',
    ABANDONED = 'ABANDONED',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED',
    UNKNOWN = 'UNKNOWN'
}

export enum JobPriority {
    CRITICAL = 'CRITICAL',
    HIGH = 'HIGH',
    NORMAL = 'NORMAL',
    LOW = 'LOW',
    BACKGROUND = 'BACKGROUND'
}

export enum JobEventType {
    SUBMITTED = 'SUBMITTED',
    STARTED = 'STARTED',
    PROGRESS = 'PROGRESS',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED',
    CANCELLED = 'CANCELLED',
    RETRYING = 'RETRYING',
    TIMED_OUT = 'TIMED_OUT',
    PAUSED = 'PAUSED',
    RESUMED = 'RESUMED'
}

export interface JobExecutionStatus {
    state: JobState;
    batchStatus: BatchStatus;
    percentComplete: number;
    message: string;  // human-readable status message
    errorMessage: string; // detailed error message if job failed
    submittedAt: string; // timestamp when the job was submitted
    startedAt: string; // timestamp when the job was started
    completedAt: string; // timestamp when the job was completed
    estimatedTimeRemaining: string; // estimated time remaining for the job
    retryCount: number;
}

export interface JobSubmisionRequest {
    jobType: string;
    parameters: any;
    priority: JobPriority;
    correlationId: string;
    callbackurl: string; // Webhook URL for completion notification
    enableSSE: boolean;  // Enable Server-Sent Events for real-time updates
    notificationPrefs: any;  // Notification preferences for job updates
}

export interface JobResult {
    success: boolean;
    summary: string;
    details: any;
    itemsProcessed: number;
    executionDurationMillis: number;
    errorCount: number;
}

export interface JobExecutionContext {
    executionId: number;
    jobInstanceId: number;
    jobType: string;
    correlationId: string;
    status: JobExecutionStatus;
    result: JobResult;
    events: Array<JobEventType>;
    request: JobSubmisionRequest;
    submittedBy: string;
    createdAt: string;
    updatedAt: string;
}
